import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { formatDurationShort } from '../lib/format.js';
import { getPins, togglePin } from '../lib/pins.js';
import TaskTypePicker from './TaskTypePicker.jsx';

const STATUS_COLOR = {
  'To Do': 'text-blue-700 bg-blue-50 dark:text-blue-300 dark:bg-blue-500/10',
  'In Progress': 'text-cyan-700 bg-cyan-50 dark:text-cyan-300 dark:bg-cyan-500/10',
  'Blocked': 'text-danger bg-danger/10',
  'Complete': 'text-running bg-running/10',
  'On Hold': 'text-warn bg-warn/10',
};

// Custom status ordering for the "Status" sort. Statuses outside this
// list fall to the bottom and sort alphabetically among themselves.
const STATUS_ORDER = ['In Progress', 'On Hold', 'Blocked', 'To Do', 'Complete'];
const STATUS_RANK = new Map(STATUS_ORDER.map((s, i) => [s, i]));

const SORT_KEY = 'tt_sort_v1';
const SORT_OPTIONS = [
  { id: 'recent',    label: 'Recent' },
  { id: 'timeSpent', label: 'Time spent' },
  { id: 'status',    label: 'Status' },
];

function loadSort() {
  const v = localStorage.getItem(SORT_KEY);
  return SORT_OPTIONS.some(o => o.id === v) ? v : 'recent';
}

function compareForSort(a, b, sort) {
  if (sort === 'timeSpent') {
    return (b.totalSeconds || 0) - (a.totalSeconds || 0);
  }
  if (sort === 'status') {
    const ra = STATUS_RANK.has(a.status) ? STATUS_RANK.get(a.status) : STATUS_ORDER.length;
    const rb = STATUS_RANK.has(b.status) ? STATUS_RANK.get(b.status) : STATUS_ORDER.length;
    if (ra !== rb) return ra - rb;
    // Fall back to alpha within a status bucket.
    return (a.status || '').localeCompare(b.status || '') || (a.name || '').localeCompare(b.name || '');
  }
  // 'recent' — the API already returns Created Date desc, so preserve
  // the server order. We stable-sort by treating equal everywhere.
  return 0;
}

function StarButton({ filled, onClick }) {
  return (
    <button
      onClick={e => { e.stopPropagation(); onClick(); }}
      className="p-1.5 rounded-md hover:bg-panel transition-colors flex-shrink-0"
      aria-label={filled ? 'Unpin' : 'Pin'}
    >
      <svg
        width="16" height="16" viewBox="0 0 24 24"
        fill={filled ? '#f59e0b' : 'none'}
        stroke={filled ? '#f59e0b' : 'currentColor'}
        strokeWidth="2" strokeLinejoin="round"
        className={filled ? '' : 'text-muted hover:text-warn transition-colors'}
      >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    </button>
  );
}

function TaskRow({ task, pinned, completing, queueState, onSelect, onTogglePin, onComplete }) {
  const isRunning = queueState === 'running';
  const isPaused = queueState === 'paused';
  return (
    <div
      className={`flex items-center transition-colors group ${
        isRunning
          ? 'bg-running/15 hover:bg-running/20 border-l-2 border-running'
          : isPaused
            ? 'bg-warn/10 hover:bg-warn/15 border-l-2 border-warn'
            : 'hover:bg-panel2 border-l-2 border-transparent'
      }`}
    >
      <div className="pl-3 pr-1 flex items-center gap-1">
        {isRunning && <div className="w-1.5 h-1.5 rounded-full bg-running pulse-dot" title="Active timer" />}
        {isPaused && <div className="w-1.5 h-1.5 rounded-full bg-warn" title="On hold" />}
        <StarButton filled={pinned} onClick={onTogglePin} />
      </div>
      <button
        onClick={onSelect}
        className="flex-1 min-w-0 text-left pl-2 pr-2 py-3 flex items-center justify-between gap-3"
      >
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{task.name}</div>
          <div className="flex items-center gap-2 mt-1.5">
            {task.status && (
              <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${STATUS_COLOR[task.status] || 'text-muted bg-panel2'}`}>
                {task.status}
              </span>
            )}
            <span className="text-xs text-muted">
              {task.subTaskIds.length} sub-task{task.subTaskIds.length === 1 ? '' : 's'}
            </span>
            {task.totalSeconds > 0 && (
              <span className="text-xs text-muted font-mono">
                · {formatDurationShort(task.totalSeconds)}
              </span>
            )}
          </div>
        </div>
      </button>
      {task.status !== 'Complete' && onComplete && (
        <button
          onClick={e => { e.stopPropagation(); onComplete(task); }}
          disabled={completing}
          title="Mark task complete"
          className="p-1.5 mr-1 rounded-md hover:bg-running/10 border border-transparent hover:border-running/30 transition-colors text-muted hover:text-running"
        >
          {completing ? (
            <span className="text-[11px] font-mono">...</span>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <path d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>
      )}
      <button onClick={onSelect} className="pr-3 py-3 group" aria-label="Open task">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted group-hover:text-accent transition-colors">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    </div>
  );
}

const HIDDEN_STATUSES_FOR_MINE = new Set(['Complete']);

export default function TaskSearch({
  onSelect, uid, name, onCompleteTask, completingTaskIds, queue,
  taskFormUrl,
  // subtaskFormUrl deliberately removed: the New Sub-Task surface is
  // gone. All task creation goes through TaskTypePicker which lets the
  // existing Airtable automation build the sub-tasks for the chosen
  // Task Type.
}) {
  const queueByTaskId = new Map((queue || []).filter(q => q.taskId).map(q => [q.taskId, q.state]));
  const [query, setQuery] = useState('');
  const [tasks, setTasks] = useState([]);
  const [pinnedTasks, setPinnedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState(loadSort);
  const lastReqId = useRef(0);
  const [pinTick, setPinTick] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);

  const pinnedIds = useMemo(() => getPins(uid), [uid, pinTick]);

  useEffect(() => {
    localStorage.setItem(SORT_KEY, sort);
  }, [sort]);

  useEffect(() => {
    if (!pinnedIds.length) {
      setPinnedTasks([]);
      return;
    }
    api.tasksByIds(pinnedIds)
      .then(d => setPinnedTasks(d.tasks))
      .catch(e => console.warn('pinned fetch failed', e));
  }, [pinnedIds]);

  useEffect(() => {
    const reqId = ++lastReqId.current;
    const handle = setTimeout(() => {
      setLoading(true);
      setError(null);
      api.tasks(query, { mine: uid, ownerName: name })
        .then(d => { if (lastReqId.current === reqId) setTasks(d.tasks); })
        .catch(e => { if (lastReqId.current === reqId) setError(e.message); })
        .finally(() => { if (lastReqId.current === reqId) setLoading(false); });
    }, 200);
    return () => clearTimeout(handle);
  }, [query, uid, name]);

  const handleTogglePin = id => {
    togglePin(uid, id);
    setPinTick(t => t + 1);
  };

  const pinnedSet = new Set(pinnedIds);
  const visibleTasks = !query
    ? tasks.filter(t => !HIDDEN_STATUSES_FOR_MINE.has(t.status))
    : tasks;
  const unpinnedTasks = visibleTasks.filter(t => !pinnedSet.has(t.id));
  const sortedTasks = sort === 'recent'
    ? unpinnedTasks
    : [...unpinnedTasks].sort((a, b) => compareForSort(a, b, sort));

  const openExternal = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div>
      <div className="relative">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="absolute left-4 top-1/2 -translate-y-1/2 text-muted pointer-events-none">
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <input
          autoFocus
          type="text"
          placeholder="Search your tasks..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          className="w-full pl-11 pr-4 py-3.5 rounded-lg bg-panel border border-border focus:border-accent focus:outline-none text-base placeholder:text-muted"
        />
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        <label className="inline-flex items-center gap-2 text-xs text-muted">
          Sort by
          <select
            value={sort}
            onChange={e => setSort(e.target.value)}
            className="px-2 py-1 rounded-md bg-panel border border-border text-text focus:border-accent focus:outline-none"
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-3">
          {/* Primary path: in-app TaskTypePicker. Builds a task + its
              sub-tasks via the existing Airtable automation, all
              without leaving the app. */}
          <button
            onClick={() => setPickerOpen(true)}
            disabled={pickerOpen}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent hover:bg-accent2 text-white text-xs font-semibold transition-colors disabled:opacity-50"
            title="Create a task from one of the approved Task Types"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <path d="M12 5v14M5 12h14" />
            </svg>
            New task
          </button>
          {/* Secondary path: the Airtable intake form, for non-standard
              tasks that don't fit any Task Type. Rendered as a smaller
              link to make clear it's the escape hatch, not the default. */}
          {taskFormUrl && (
            <a
              href={taskFormUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted hover:text-accent underline-offset-2 hover:underline transition-colors"
              title="Open the Airtable Form for a custom (non-templated) task"
            >
              Use intake form ↗
            </a>
          )}
        </div>
      </div>

      {pickerOpen && (
        <div className="mt-3">
          <TaskTypePicker
            uid={uid}
            fallbackFormUrl={taskFormUrl}
            onCancel={() => setPickerOpen(false)}
            onCreated={task => {
              setPickerOpen(false);
              if (task && onSelect) onSelect(task);
            }}
          />
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 rounded-lg border border-danger/30 bg-danger/10 text-sm text-danger flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setQuery(q => q + '')} className="underline">Retry</button>
        </div>
      )}

      {pinnedTasks.length > 0 && !query && (
        <div className="mt-3 rounded-lg border border-border bg-panel overflow-hidden">
          <div className="px-4 py-2 border-b border-border bg-panel2/40 text-[10px] uppercase tracking-wider text-muted flex items-center gap-1.5">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="2" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            Pinned · {pinnedTasks.length}
          </div>
          <div className="divide-y divide-border">
            {pinnedTasks.map(t => (
              <TaskRow
                key={t.id}
                task={t}
                pinned
                completing={Boolean(completingTaskIds?.[t.id])}
                queueState={queueByTaskId.get(t.id)}
                onSelect={() => onSelect(t)}
                onTogglePin={() => handleTogglePin(t.id)}
                onComplete={onCompleteTask}
              />
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 rounded-lg border border-border bg-panel divide-y divide-border max-h-[55vh] overflow-y-auto">
        {loading && sortedTasks.length === 0 && (
          <div className="p-6 text-center text-muted text-sm">Loading...</div>
        )}
        {!loading && sortedTasks.length === 0 && (
          <div className="p-6 text-center text-muted text-sm">
            {query
              ? 'No tasks match your search'
              : pinnedTasks.length
                ? 'No other tasks'
                : taskFormUrl
                  ? 'No active tasks. Click + New task to start one.'
                  : 'No active tasks.'}
          </div>
        )}
        {sortedTasks.map(t => (
          <TaskRow
            key={t.id}
            task={t}
            pinned={pinnedSet.has(t.id)}
            completing={Boolean(completingTaskIds?.[t.id])}
            queueState={queueByTaskId.get(t.id)}
            onSelect={() => onSelect(t)}
            onTogglePin={() => handleTogglePin(t.id)}
            onComplete={onCompleteTask}
          />
        ))}
      </div>
    </div>
  );
}
